import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { FilterVal } from 'src/interfaces/iproptypes';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { findKeyByValue, isEmpty, parseFilterValToApiResponse } from 'src/utils/functions';
import { COLOR_E } from 'src/utils/const';
import LockSharpIcon from '@material-ui/icons/LockSharp';
import { ZoomButtons } from 'src/App/Dashboards/Market/Pages/OverviewPage/UtilityComponents/ZoomButtons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchVisibleMapSupply } from 'src/store/Dashboard/actions';
import AutohideSnackbar from 'src/App/Dashboards/Market/Components/Alerts/AutohideSnackbar';

const bedroomLabelToIndexMap = {
  Any: 100,
  Studio: 0,
  '1': 1,
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6+': '6+',
  private_room: 'p',
  shared_room: 's',
  hotel_room: 'h',
};

const MapFilter = ({
  search,
  toggleDrawer,
  filterVal,
  setFilterVal,
  filterActive,
  setFilterActive,
  isDisabled,
  handleOnSampleClick,
  setZoom,
  onlyShowZoom,
  onZoomInClick,
  dashboardLayoutView,
  noDataInMarket,
  bounds,
  callDynamicMarkers,
  marketID,
  showFilterComingSoonAlert,
  setShowFilterComingSoonAlert,
  filterCacheLoading,
  listingSortingKey,
}: {
  search: any;
  toggleDrawer: () => void;
  filterVal: FilterVal;
  setFilterVal: React.Dispatch<React.SetStateAction<FilterVal>>;
  filterActive: boolean;
  setFilterActive: React.Dispatch<React.SetStateAction<boolean>>;
  isDisabled: boolean;
  handleOnSampleClick: any;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
  onlyShowZoom: boolean;
  onZoomInClick: () => void;
  dashboardLayoutView: 'map' | 'dashboard' | 'discovery' | 'compare' | 'advance' | 'searchListing';
  noDataInMarket: boolean;
  bounds: any;
  callDynamicMarkers: (boundsObj: any, bedroomFilter: any) => Promise<void>;
  marketID: number | string;
  showFilterComingSoonAlert: boolean | 'open';
  setShowFilterComingSoonAlert: any;
  filterCacheLoading: boolean;
  listingSortingKey: string;
}) => {
  const dispatch = useDispatch();
  const isUserAllowed = useSelector(state => (state as any).dashboard.isUserAllowed);
  const [selectedBedrooms, setSelectedBedrooms] = useState<string[]>(['Any']);
  const [showSnackbar, setShowSnackbar] = useState(false);
  let addRoomType: string[] = [];
  const useFullMarkers = useSelector(state => (state as any).dashboard.useFullMarkers);

  useEffect(() => {
    if (showSnackbar) {
      setTimeout(() => {
        setShowSnackbar(false);
      }, 10000);
    }
  }, [showSnackbar]);

  const updateSelectedBedrooms = (bedroom: string) => {
    let updatedBedrooms = [...selectedBedrooms];
    if (updatedBedrooms.includes(bedroom)) {
      updatedBedrooms.splice(updatedBedrooms.indexOf(bedroom), 1);
    } else {
      updatedBedrooms.push(bedroom);
    }
    if (bedroom === 'Any') {
      updatedBedrooms = ['Any'];
    } else if (bedroom !== 'Any' && updatedBedrooms.includes('Any')) {
      updatedBedrooms.splice(updatedBedrooms.indexOf('Any'), 1);
    }

    setSelectedBedrooms(updatedBedrooms);
    return updatedBedrooms;
  };

  useEffect(() => {
    if (filterActive) {
      setSelectedBedrooms(
        filterVal.bedrooms.map(bedroom => findKeyByValue(bedroomLabelToIndexMap, bedroom) as string)
      );
    } else {
      setSelectedBedrooms(['Any']);
    }
  }, [filterActive, filterVal]);

  const handleBedroomButtonClick = async (bedroom: string) => {
    if (showFilterComingSoonAlert) {
      showFilterComingSoonAlert !== 'open' && setShowFilterComingSoonAlert('open');
      return;
    }
    const updatedBedrooms = updateSelectedBedrooms(bedroom);
    setShowSnackbar(true);
    setFilterVal((prevFilterVal: FilterVal) => {
      const newFilterVal = {
        ...prevFilterVal,
        bedrooms: updatedBedrooms.map(selectedBedroom => bedroomLabelToIndexMap[selectedBedroom]),
      };

      const { filters } =
        filterCacheLoading || search.data.isSubmarket || showFilterComingSoonAlert || !isUserAllowed
          ? { filters: {} }
          : parseFilterValToApiResponse(newFilterVal);
      dispatch(
        fetchVisibleMapSupply(
          marketID,
          search.data.polygon || search.data.multiPolygon,
          bounds,
          filters,
          listingSortingKey
        )
      );

      return newFilterVal;
    });

    setFilterActive(true);
  };

  return (
    <Box className="mapFilterContainer" sx={{ pointerEvents: 'auto' }}>
      {showSnackbar && (
        <AutohideSnackbar
          message="Heads up: Filters only apply to the listings you see on the map — they won't change the heatmap or cluster visuals."
          type="info"
        />
      )}
      <Grid container sx={{ transform: 'scale(0.5)', transformOrigin: 'bottom right' }}>
        <Grid container item xs={12} justifyContent={'flex-end'}>
          <ZoomButtons
            size={dashboardLayoutView === 'dashboard' ? 100 : 80}
            setZoom={setZoom}
            zoomInAction={() => onZoomInClick()}
          />
        </Grid>
        {!onlyShowZoom && (
          <>
            <Grid container item xs={12} justifyContent={'flex-end'}>
              <Box className="mapFilterInfoBox">
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 800, textShadow: '0 0 10px #fff', fontSize: '26px' }}
                >
                  Listing Filter{' '}
                  {isDisabled ? <LockSharpIcon style={{ fontSize: 27, color: 'inherit' }} /> : ''}
                </Typography>
              </Box>
            </Grid>
            <Grid container item xs={12} spacing={1} justifyContent={'flex-end'}>
              {['Any', 'Studio', '1', '2', '3', '4', '5', '6+'].map((bedroom, index) => (
                <Grid item key={'filter-bedroom-' + bedroom}>
                  <Button
                    className={
                      selectedBedrooms.includes(bedroom)
                        ? 'mapFilterButton--active'
                        : 'mapFilterButton'
                      // filterVal.bedrooms.includes(bedroomLabelToIndexMap[bedroom]) ? 'mapFilterButton--active' : 'mapFilterButton'
                    }
                    onClick={async () =>
                      isDisabled
                        ? handleOnSampleClick('expert')
                        : await handleBedroomButtonClick(bedroom)
                    }
                    // disabled={!isUserAllowed ? true : false}
                  >
                    <span>
                      {isDisabled ? <LockSharpIcon style={{ marginRight: '2px' }} /> : <></>}
                    </span>
                    <span className={index > 2 ? 'notranslate' : ''}>{bedroom}</span>
                  </Button>
                </Grid>
              ))}
              {/* <Grid item>
                <Button className="mapFilterButton" onClick={() => toggleDrawer()}>
                  <FilterAltOutlinedIcon sx={{ fontSize: 34 }} />
                  All Filters
                </Button>
              </Grid> */}
              {noDataInMarket && (
                <Typography
                  sx={{
                    position: 'absolute',
                    top: '85%',
                    left: '45%',
                    fontSize: '37px',
                    fontWeight: 900,
                    color: COLOR_E,
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                  }}
                >
                  No Data Available
                </Typography>
              )}
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default MapFilter;
